import React, { CSSProperties, ReactNode } from 'react'

import styles from '../../Grid.module.scss'

type Gutter = number | [number, number] | Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl', number>>

export interface RowProps {
	children?: ReactNode
	gutter?: Gutter
	justify?: 'start' | 'end' | 'center' | 'space-around' | 'space-between' | 'space-evenly'
	align?: 'top' | 'middle' | 'bottom' | 'stretch'
	style?: CSSProperties
	className?: string
}

const getGutterValue = (gutter: Gutter | undefined): [number, number] => {
	if (!gutter) return [0, 0]
	if (typeof gutter === 'number') return [gutter, gutter]
	if (Array.isArray(gutter)) return gutter

	const breakpoints: Array<keyof typeof gutter> = ['xxl', 'xl', 'lg', 'md', 'sm', 'xs']
	const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1920

	const breakpointValues = {
		xs: 0,
		sm: 576,
		md: 768,
		lg: 992,
		xl: 1200,
		xxl: 1600
	}

	for (const bp of breakpoints) {
		const value = gutter[bp]
		if (value !== undefined && screenWidth >= breakpointValues[bp]) {
			return [value, value]
		}
	}

	return [0, 0]
}

export const Row: React.FC<RowProps> = ({ children, gutter, justify, align, style, className }) => {
	const [horizontalGutter, verticalGutter] = getGutterValue(gutter)

	const rowStyle: CSSProperties = {
		'--row-gap': `${verticalGutter}px`,
		'--col-gap': `${horizontalGutter}px`,
		...style
	} as React.CSSProperties

	const classNames = [
		styles.row,
		justify && styles[`row--justify-${justify}`],
		align && styles[`row--align-${align}`],
		className
	]
		.filter(Boolean)
		.join(' ')

	return (
		<div className={classNames} style={rowStyle}>
			{React.Children.map(children, child =>
				React.isValidElement(child)
					? React.cloneElement(child, {
							...child.props,
							_gutter: [horizontalGutter, verticalGutter]
					  } as any)
					: child
			)}
		</div>
	)
}
